/**
 * Child Profile Page ("Me") — Kid-Friendly Redesign
 * Warm, personal profile with mascot, kid colors, and playful personality
 */

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Calendar, GraduationCap, BookOpen, Flame, Star } from 'lucide-react';
import { SUBJECT_DISPLAY_NAMES, SUBJECT_ICONS } from '@kids-lms/constants';
import type { Subject } from '@kids-lms/types';
import { useChildStore, useSoundEffects } from '../hooks';
import { useAuthStore } from '../hooks/useAuthStore';
import { springs, staggerContainer, staggerItem } from '../animation';
import MindoraCharacter from '../components/character/MindoraCharacter';
import SpeechBubble from '../components/ui/SpeechBubble';
import AnimatedNumber from '../components/ui/AnimatedNumber';
import { SparkleCluster, DecorativeStar, DecorativeCloud, WavyDivider } from '../components/decorations';
import { kidPalette } from '../styles/kidPalette';

function calculateAge(dateOfBirth: string | Date): number {
  const dob = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
}

const gradeLabels: Record<number, string> = {
  0: 'Pre-K', 1: '1st Grade', 2: '2nd Grade', 3: '3rd Grade',
  4: '4th Grade', 5: '5th Grade', 6: '6th Grade', 7: '7th Grade',
  8: '8th Grade', 9: '9th Grade', 10: '10th Grade', 11: '11th Grade', 12: '12th Grade',
};

export default function ChildProfilePage() {
  const { selectedChild, streakData } = useChildStore();
  const { logout } = useAuthStore();
  const { play } = useSoundEffects();

  const age = useMemo(() => {
    if (!selectedChild?.dateOfBirth) return null;
    return calculateAge(selectedChild.dateOfBirth);
  }, [selectedChild?.dateOfBirth]);

  const initials = useMemo(() => {
    if (!selectedChild?.name) return '?';
    return selectedChild.name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }, [selectedChild?.name]);

  const name = selectedChild?.name || 'Friend';

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-6 pb-24 relative">
      {/* Background decorations */}
      <DecorativeCloud size={70} className="absolute top-8 right-2 opacity-20" />
      <DecorativeCloud size={50} className="absolute top-52 left-0 opacity-15" variant="wispy" />
      <DecorativeStar size={18} color={kidPalette.dandelion} pulse className="absolute top-16 left-6" />
      <DecorativeStar size={12} color={kidPalette.berry} pulse className="absolute top-44 right-8" />

      {/* Profile Card */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={springs.gentle}
        className="bg-kid-surface rounded-kid shadow-kid-md border-2 border-kid-border overflow-hidden mb-6"
      >
        {/* Header with kid-friendly background */}
        <div className="bg-kid-bg px-6 pt-6 pb-10 text-center relative overflow-hidden">
          {/* Decorative shapes */}
          <DecorativeCloud size={60} className="absolute top-2 left-4 opacity-30" />
          <DecorativeCloud size={40} className="absolute top-4 right-8 opacity-20" variant="wispy" />
          <DecorativeStar size={16} color={kidPalette.dandelion} pulse className="absolute top-3 right-4" />
          <DecorativeStar size={12} color={kidPalette.berry} pulse className="absolute bottom-4 left-8" />
          <SparkleCluster count={4} spread={20} className="absolute top-2 left-1/4" />

          <div className="relative z-10">
            {/* Mascot */}
            <div className="flex justify-center mb-2">
              <MindoraCharacter mood="happy" size="lg" />
            </div>

            {/* Avatar */}
            {selectedChild?.avatarUrl ? (
              <img
                src={selectedChild.avatarUrl}
                alt={selectedChild.name}
                className="w-20 h-20 rounded-full mx-auto border-4 border-kid-surface object-cover shadow-kid"
              />
            ) : (
              <div
                className="w-20 h-20 rounded-full mx-auto border-4 border-kid-surface flex items-center justify-center shadow-kid"
                style={{ backgroundColor: kidPalette.violet }}
              >
                <span className="text-2xl font-display text-white">{initials}</span>
              </div>
            )}
            <h1 className="font-display text-kid-2xl text-kid-text mt-3">
              {name}
            </h1>
            {age !== null && (
              <p className="text-kid-text-soft font-kid text-kid-sm">{age} years old</p>
            )}
          </div>
        </div>

        {/* Speech bubble */}
        <div className="px-5 -mt-4 relative z-10">
          <SpeechBubble className="mb-4">
            <p className="text-kid-text-soft font-kid text-kid-sm text-center">
              This is all about you, {name}!
            </p>
          </SpeechBubble>
        </div>

        {/* Stats strip */}
        <motion.div
          variants={staggerContainer()}
          initial="initial"
          animate="animate"
          className="grid grid-cols-3 px-4 pb-2"
        >
          <motion.div
            variants={staggerItem}
            className="rounded-kid-sm p-3 text-center mx-1"
            style={{ backgroundColor: `${kidPalette.dandelion}15` }}
          >
            <Flame className="w-5 h-5 mx-auto mb-1" style={{ color: kidPalette.tangerine }} />
            <p className="font-display text-kid-lg text-kid-text">
              <AnimatedNumber value={streakData?.habitStreak || 0} />
            </p>
            <p className="text-kid-xs text-kid-text-soft font-kid font-bold">Streak</p>
          </motion.div>
          <motion.div
            variants={staggerItem}
            className="rounded-kid-sm p-3 text-center mx-1"
            style={{ backgroundColor: `${kidPalette.ocean}15` }}
          >
            <Star className="w-5 h-5 mx-auto mb-1" style={{ color: kidPalette.dandelion }} />
            <p className="font-display text-kid-lg text-kid-text">
              <AnimatedNumber value={streakData?.habitPoints || 0} />
            </p>
            <p className="text-kid-xs text-kid-text-soft font-kid font-bold">Points</p>
          </motion.div>
          <motion.div
            variants={staggerItem}
            className="rounded-kid-sm p-3 text-center mx-1"
            style={{ backgroundColor: `${kidPalette.violet}15` }}
          >
            <GraduationCap className="w-5 h-5 mx-auto mb-1" style={{ color: kidPalette.violet }} />
            <p className="font-display text-kid-lg text-kid-text">
              {selectedChild?.gradeLevel !== undefined
                ? selectedChild.gradeLevel
                : '-'}
            </p>
            <p className="text-kid-xs text-kid-text-soft font-kid font-bold">Grade</p>
          </motion.div>
        </motion.div>

        <WavyDivider color={kidPalette.ocean} className="mx-4" />

        <div className="px-5 pt-4 pb-5 space-y-4">
          {/* Info rows */}
          {selectedChild?.dateOfBirth && (
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${kidPalette.berry}15` }}
              >
                <Calendar className="w-4 h-4" style={{ color: kidPalette.berry }} />
              </div>
              <span className="text-kid-text-soft font-kid text-kid-sm">Birthday</span>
              <span className="ml-auto text-kid-text font-kid font-bold text-kid-sm">
                {new Date(selectedChild.dateOfBirth).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </div>
          )}

          {selectedChild?.gradeLevel !== undefined && (
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${kidPalette.violet}15` }}
              >
                <GraduationCap className="w-4 h-4" style={{ color: kidPalette.violet }} />
              </div>
              <span className="text-kid-text-soft font-kid text-kid-sm">Grade</span>
              <span className="ml-auto text-kid-text font-kid font-bold text-kid-sm">
                {gradeLabels[selectedChild.gradeLevel] || `Grade ${selectedChild.gradeLevel}`}
              </span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Allowed Subjects */}
      {selectedChild?.allowedSubjects && selectedChild.allowedSubjects.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springs.gentle, delay: 0.1 }}
          className="bg-kid-surface rounded-kid shadow-kid border-2 border-kid-border p-5 mb-6"
        >
          <h2 className="font-display text-kid-lg text-kid-text mb-3 flex items-center gap-2">
            <BookOpen className="w-5 h-5" style={{ color: kidPalette.violet }} />
            My Subjects
          </h2>
          <div className="flex flex-wrap gap-2">
            {selectedChild.allowedSubjects.map((subject, i) => {
              const colors = [kidPalette.ocean, kidPalette.violet, kidPalette.berry, kidPalette.meadow, kidPalette.dandelion];
              const color = colors[i % colors.length];
              return (
                <span
                  key={subject}
                  className="inline-flex items-center gap-1.5 font-kid font-bold text-kid-sm px-3 py-1.5 rounded-kid-sm border-2"
                  style={{
                    backgroundColor: `${color}15`,
                    borderColor: `${color}30`,
                    color: kidPalette.text,
                  }}
                >
                  <span>{SUBJECT_ICONS[subject as Subject] || '📚'}</span>
                  {SUBJECT_DISPLAY_NAMES[subject as Subject] || subject}
                </span>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Daily Time Limit */}
      {selectedChild?.dailyTimeLimit && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springs.gentle, delay: 0.15 }}
          className="rounded-kid shadow-kid border-2 border-kid-border p-5 mb-6"
          style={{ backgroundColor: `${kidPalette.sky}15` }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${kidPalette.ocean}20` }}
            >
              <span className="text-xl">⏰</span>
            </div>
            <div>
              <p className="font-kid font-bold text-kid-sm text-kid-text">Daily Learning Time</p>
              <p className="text-kid-xs text-kid-text-soft font-kid">
                {selectedChild.dailyTimeLimit} minutes per day
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Friendly Logout */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ ...springs.gentle, delay: 0.2 }}
        onClick={() => { play('navigate'); logout(); }}
        className="w-full flex items-center justify-center gap-3 bg-kid-surface rounded-kid shadow-kid border-2 border-kid-border p-4 font-kid font-bold text-kid-text-soft hover:bg-kid-bg transition-colors touch-target"
      >
        <MindoraCharacter mood="waving" size="sm" />
        <span>Bye bye!</span>
        <span className="text-lg">👋</span>
      </motion.button>
    </div>
  );
}
